import { useEffect, useState, useCallback } from "react";
import { API_BASE } from "../lib/api";

const ADMIN_KEY = import.meta.env.VITE_ADMIN_KEY;

// ══════════════════════════════════════════════════════════════════════
// Types
// ══════════════════════════════════════════════════════════════════════

interface Gate {
  gate: number;
  name: string;
  metric: number | string;
  threshold_green: string;
  threshold_red: string;
  status: "green" | "amber" | "red";
  why: string;
}

interface GatesResponse {
  gates: Gate[];
  overall_status: string;
  message: string;
  requirement: string;
}

interface BurnResponse {
  weekly_total_burn: number;
  weekly_active_users: number;
  cost_per_user_per_week: number;
  burn_status: string;
  cost_per_user_status: string;
  thresholds: {
    burn_amber: number;
    burn_red: number;
    cpu_amber: number;
    cpu_red: number;
  };
  daily_breakdown: Array<{
    date: string;
    total_cost: number;
    active_users: number;
    cost_per_user: number;
    alert_status: string;
  }>;
}

interface HVUResponse {
  hvu_count: number;
  status: string;
  thresholds: Record<string, string>;
  top_hvus: Array<{
    user_id: string;
    email_hint: string;
    criteria_met: number;
    criteria: string[];
    transcript_count: number;
    hours_month: number;
    msgs_month: number;
  }>;
}

interface AbuseFlag {
  flag_id: string;
  user_id: string;
  flag_type: string;
  detected_at: string;
  metric_value: number;
  threshold: number;
  reviewed: boolean;
  review_outcome?: string;
  notes?: string;
}

interface ActivationResponse {
  median_time_to_first_transcript_hours: number;
  time_status: string;
  week1_activation_pct: number;
  week1_status: string;
  funnel: Array<{
    step: string;
    conversion_pct: number;
    status: string;
    target: number;
  }>;
}

interface RetentionResponse {
  week4_retention_pct: number;
  week4_status: string;
  session_frequency: {
    daily: number;
    weekly: number;
    monthly: number;
  };
  avg_transcripts_at_churn: number;
  cohorts: Array<{
    signup_week: string;
    signups: number;
    week1_pct: number;
    week4_pct: number;
  }>;
}

// ══════════════════════════════════════════════════════════════════════
// Helper Components
// ══════════════════════════════════════════════════════════════════════

function StatusBadge({ status }: { status: string }) {
  const colors = {
    green: "bg-green-100 text-green-800",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-800",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status as keyof typeof colors] || colors.red}`}>
      {status === "green" ? "🟢" : status === "amber" ? "🟡" : "🔴"} {status.toUpperCase()}
    </span>
  );
}

function MetricCard({
  title,
  value,
  subtitle,
  status,
  large,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  status?: string;
  large?: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        {status && <StatusBadge status={status} />}
      </div>
      <p className={`mt-2 font-semibold text-gray-900 ${large ? "text-3xl" : "text-2xl"}`}>
        {value}
      </p>
      {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Main Component
// ══════════════════════════════════════════════════════════════════════

export default function AdminMetricsView() {
  const [gates, setGates] = useState<GatesResponse | null>(null);
  const [burn, setBurn] = useState<BurnResponse | null>(null);
  const [hvu, setHvu] = useState<HVUResponse | null>(null);
  const [abuse, setAbuse] = useState<AbuseFlag[]>([]);
  const [activation, setActivation] = useState<ActivationResponse | null>(null);
  const [retention, setRetention] = useState<RetentionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState<"overview" | "activation" | "retention" | "abuse">("overview");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    
    const headers = { "X-Admin-Key": ADMIN_KEY };
    
    try {
      const [gatesRes, burnRes, hvuRes, abuseRes, activationRes, retentionRes] = await Promise.all([
        fetch(`${API_BASE}/admin/dashboard/gates`, { headers }),
        fetch(`${API_BASE}/admin/dashboard/burn`, { headers }),
        fetch(`${API_BASE}/admin/dashboard/hvu-count`, { headers }),
        fetch(`${API_BASE}/admin/dashboard/abuse-flags`, { headers }),
        fetch(`${API_BASE}/admin/dashboard/activation`, { headers }),
        fetch(`${API_BASE}/admin/dashboard/retention`, { headers }),
      ]);

      if (gatesRes.ok) setGates(await gatesRes.json());
      if (burnRes.ok) setBurn(await burnRes.json());
      if (hvuRes.ok) setHvu(await hvuRes.json());
      if (abuseRes.ok) {
        const abuseData = await abuseRes.json();
        setAbuse(abuseData.flags || []);
      }
      if (activationRes.ok) setActivation(await activationRes.json());
      if (retentionRes.ok) setRetention(await retentionRes.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load metrics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
        {error}
        <button onClick={fetchAll} className="ml-3 font-medium underline hover:text-red-800">
          Retry
        </button>
      </div>
    );
  }

  const unreviewedAbuse = abuse.filter(f => !f.reviewed);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">User Metrics</h2>
        <button
          onClick={fetchAll}
          className="rounded-md border border-gray-300 p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
          title="Refresh"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H4.598a.75.75 0 0 0-.75.75v3.634a.75.75 0 0 0 1.5 0v-2.033l.312.311a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.06-7.19a.75.75 0 0 0-1.5 0v2.033l-.312-.312A7 7 0 0 0 2.848 9.093a.75.75 0 0 0 1.449.39A5.5 5.5 0 0 1 13.89 7.11l.311.312h-2.432a.75.75 0 0 0 0 1.5h3.634a.75.75 0 0 0 .75-.75V4.538Z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {[
          { id: "overview", label: "80/20 Overview" },
          { id: "activation", label: "Activation" },
          { id: "retention", label: "Retention" },
          { id: "abuse", label: `Abuse ${unreviewedAbuse.length > 0 ? `(${unreviewedAbuse.length})` : ""}` },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id as typeof activeSection)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              activeSection === tab.id
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* OVERVIEW (80/20) */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeSection === "overview" && (
        <div className="space-y-6">
          {/* 3 Core Metrics */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">📊 The 3 Core Metrics (80/20 View)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <MetricCard
                title="Metric 1: Burn per User"
                value={burn ? `$${(burn.cost_per_user_per_week ?? 0).toFixed(2)}/wk` : "—"}
                subtitle={burn ? `Total burn: $${(burn.weekly_total_burn ?? 0).toFixed(2)}/wk` : undefined}
                status={burn?.cost_per_user_status}
                large
              />
              <MetricCard
                title="Metric 2: 10-Transcript Users"
                value={hvu?.hvu_count ?? "—"}
                subtitle="Active users with 10+ transcripts"
                status={hvu?.status}
                large
              />
              <MetricCard
                title="Metric 3: Weekly Active Users"
                value={burn?.weekly_active_users ?? "—"}
                subtitle="Users active in last 7 days"
                large
              />
            </div>
          </div>

          {/* 4 Monetisation Gates */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">🎯 Monetisation Gates</h3>
              {gates && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  gates.overall_status === "READY" 
                    ? "bg-green-100 text-green-800" 
                    : gates.overall_status === "CLOSE"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-red-100 text-red-800"
                }`}>
                  {gates.overall_status}
                </span>
              )}
            </div>
            
            {gates && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {gates.gates.map((gate) => (
                    <div
                      key={gate.gate}
                      className={`rounded-lg border p-4 ${
                        gate.status === "green"
                          ? "border-green-200 bg-green-50"
                          : gate.status === "amber"
                            ? "border-amber-200 bg-amber-50"
                            : "border-red-200 bg-red-50"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900">
                            Gate {gate.gate}: {gate.name}
                          </p>
                          <p className="text-2xl font-bold mt-1">
                            {typeof gate.metric === "number" 
                              ? (gate.metric % 1 === 0 ? gate.metric : `${gate.metric}%`)
                              : gate.metric}
                          </p>
                        </div>
                        <StatusBadge status={gate.status} />
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        🟢 {gate.threshold_green} · 🔴 {gate.threshold_red}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 italic">{gate.why}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3 text-center">
                  {gates.requirement}
                </p>
              </>
            )}
          </div>

          {/* Top High-Value Users */}
          {hvu && hvu.top_hvus.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">👑 Top High-Value Users</h3>
              <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">User</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">Transcripts</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">Hours/mo</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">Chat msgs</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">Criteria</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {hvu.top_hvus.slice(0, 5).map((u) => (
                      <tr key={u.user_id}>
                        <td className="px-4 py-2 text-gray-900">{u.email_hint}</td>
                        <td className="px-4 py-2 text-gray-600">{u.transcript_count}</td>
                        <td className="px-4 py-2 text-gray-600">{(u.hours_month ?? 0).toFixed(1)}</td>
                        <td className="px-4 py-2 text-gray-600">{u.msgs_month}</td>
                        <td className="px-4 py-2">
                          <span className="text-xs text-gray-500">{u.criteria.join(", ")}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Daily Burn Chart */}
          {burn && burn.daily_breakdown.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">📈 Daily Burn (Last 7 Days)</h3>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-end gap-2 h-32">
                  {burn.daily_breakdown.slice(0, 7).reverse().map((day) => {
                    const maxCost = Math.max(...burn.daily_breakdown.map(d => d.total_cost), 1);
                    const height = (day.total_cost / maxCost) * 100;
                    return (
                      <div key={day.date} className="flex-1 flex flex-col items-center">
                        <div
                          className={`w-full rounded-t ${
                            day.alert_status === "red" ? "bg-red-400" :
                            day.alert_status === "amber" ? "bg-amber-400" : "bg-blue-400"
                          }`}
                          style={{ height: `${Math.max(height, 2)}%` }}
                          title={`$${(day.total_cost ?? 0).toFixed(2)}`}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(day.date).toLocaleDateString("en-US", { weekday: "short" })}
                        </p>
                        <p className="text-xs font-medium text-gray-700">${(day.total_cost ?? 0).toFixed(2)}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* ACTIVATION */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeSection === "activation" && activation && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <MetricCard
              title="Time to First Transcript"
              value={`${(activation.median_time_to_first_transcript_hours ?? 0).toFixed(1)} hrs`}
              subtitle="Median time from signup to first transcription"
              status={activation.time_status}
            />
            <MetricCard
              title="Week-1 Activation (3+ transcripts)"
              value={`${(activation.week1_activation_pct ?? 0).toFixed(1)}%`}
              subtitle="% of signups with 3+ transcripts in first week"
              status={activation.week1_status}
            />
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Activation Funnel</h3>
            <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
              {activation.funnel.map((step, _i) => (
                <div key={step.step}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-700">{step.step}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{(step.conversion_pct ?? 0).toFixed(0)}%</span>
                      <StatusBadge status={step.status} />
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        step.status === "green" ? "bg-green-500" :
                        step.status === "amber" ? "bg-amber-500" : "bg-red-500"
                      }`}
                      style={{ width: `${step.conversion_pct}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">Target: {step.target}%</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* RETENTION */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeSection === "retention" && retention && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <MetricCard
              title="Week-4 Retention"
              value={`${(retention.week4_retention_pct ?? 0).toFixed(1)}%`}
              subtitle="Users active 22-28 days after signup"
              status={retention.week4_status}
              large
            />
            <MetricCard
              title="Avg Transcripts at Churn"
              value={(retention.avg_transcripts_at_churn ?? 0).toFixed(1)}
              subtitle="Transcripts when users stop returning"
            />
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Session Frequency</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Daily (20+ sessions)</span>
                  <span className="font-medium">{retention.session_frequency.daily}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Weekly (4-19)</span>
                  <span className="font-medium">{retention.session_frequency.weekly}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Monthly (1-3)</span>
                  <span className="font-medium">{retention.session_frequency.monthly}</span>
                </div>
              </div>
            </div>
          </div>

          {retention.cohorts.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Cohort Retention</h3>
              <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">Signup Week</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">Signups</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">Week 1</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">Week 4</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {retention.cohorts.map((c) => (
                      <tr key={c.signup_week}>
                        <td className="px-4 py-2 text-gray-900">{c.signup_week}</td>
                        <td className="px-4 py-2 text-gray-600">{c.signups}</td>
                        <td className="px-4 py-2 text-gray-600">{(c.week1_pct ?? 0).toFixed(0)}%</td>
                        <td className="px-4 py-2">
                          <span className={`font-medium ${(c.week4_pct ?? 0) >= 30 ? "text-green-600" : (c.week4_pct ?? 0) >= 20 ? "text-amber-600" : "text-red-600"}`}>
                            {(c.week4_pct ?? 0).toFixed(0)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* ABUSE */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {activeSection === "abuse" && (
        <div className="space-y-6">
          {unreviewedAbuse.length === 0 ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-6 text-center">
              <p className="text-green-700 font-medium">✅ No unreviewed abuse flags</p>
              <p className="text-green-600 text-sm mt-1">All clear!</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">{unreviewedAbuse.length} unreviewed flag(s)</p>
              {unreviewedAbuse.map((flag) => (
                <div
                  key={flag.flag_id}
                  className="rounded-lg border border-red-200 bg-red-50 p-4"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-red-900">{flag.flag_type}</p>
                      <p className="text-sm text-red-700 mt-1">
                        User: {flag.user_id.slice(0, 8)}... · Value: {flag.metric_value} (threshold: {flag.threshold})
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        Detected: {new Date(flag.detected_at).toLocaleString()}
                      </p>
                    </div>
                    <button
                      className="px-3 py-1 text-xs font-medium text-red-700 border border-red-300 rounded hover:bg-red-100"
                      onClick={() => {
                        // TODO: Implement review modal
                        alert(`Review flag ${flag.flag_id}`);
                      }}
                    >
                      Review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* All-time abuse stats */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Abuse Detection Summary</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-gray-900">{abuse.length}</p>
                <p className="text-xs text-gray-500">Total flags</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{unreviewedAbuse.length}</p>
                <p className="text-xs text-gray-500">Unreviewed</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {abuse.filter(f => f.review_outcome === "abuser_suspended").length}
                </p>
                <p className="text-xs text-gray-500">Suspended</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {abuse.filter(f => f.review_outcome === "genuine_user").length}
                </p>
                <p className="text-xs text-gray-500">False positives</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
